import { useState } from 'react';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Target, Brain, Loader2, Building2, Calendar, Users, TrendingUp, AlertTriangle, FileCheck, Award } from 'lucide-react';
import { toast } from 'sonner';

const formatValue = (v) => {
  if (!v) return '0 RON';
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M RON`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}K RON`;
  return `${v} RON`;
};

// Extract unique suppliers from contracts → these are the COMPETITORS
function extractCompetitors(contracts) {
  const map = {};
  (contracts || []).forEach(c => {
    const suppliers = Array.isArray(c.supplier) ? c.supplier : c.supplier ? [c.supplier] : [];
    suppliers.forEach(s => {
      const parts = s.split(' - ');
      const cif = parts[0]?.replace(/\D/g, '').trim();
      const name = parts.slice(1).join(' - ').trim() || s;
      if (!cif) return;
      if (!map[cif]) map[cif] = { cif, name, contracts: 0, totalValue: 0, sources: new Set(), cpvs: new Set() };
      map[cif].contracts += 1;
      map[cif].totalValue += c.awardedValue || 0;
      map[cif].sources.add(c.source);
      map[cif].cpvs.add((c.cpvCode || '').split(' - ')[0]?.slice(0, 2));
    });
  });
  return Object.values(map)
    .map(c => ({ ...c, sources: [...c.sources], cpvs: [...c.cpvs].filter(Boolean) }))
    .sort((a, b) => b.totalValue - a.totalValue);
}

export default function CompetitorIntelligencePage() {
  const { user } = useAuth();
  const [authCif, setAuthCif] = useState('');
  const [contracts, setContracts] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dailyDate, setDailyDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0]; });
  const [dailyCpv, setDailyCpv] = useState('');
  const [dailyContracts, setDailyContracts] = useState(null);
  const [dailyCompetitors, setDailyCompetitors] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [docConstCif, setDocConstCif] = useState('');
  const [docConst, setDocConst] = useState(null);
  const [docConstLoading, setDocConstLoading] = useState(false);

  // Search contracts of an authority → extract suppliers = competitors
  const searchAuthority = async () => {
    if (!authCif.trim() || authCif.replace(/\D/g, '').length < 2) { toast.error('CIF invalid (minim 2 cifre)'); return; }
    setLoading(true); setContracts(null); setCompetitors([]); setAnalysis(null);
    try {
      const res = await API.get(`/sicap/contracts/${authCif.replace(/\D/g, '')}/latest`);
      setContracts(res.data);
      const comps = extractCompetitors(res.data.contracts);
      setCompetitors(comps);
      if (comps.length) toast.success(`${comps.length} competitori identificati din ${res.data.contracts?.length || 0} contracte`);
      else toast.info('Niciun contract gasit');
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare SICAP.ai'); }
    finally { setLoading(false); }
  };

  // Daily contracts → extract active competitors by CPV
  const searchDaily = async () => {
    setDailyLoading(true); setDailyContracts(null); setDailyCompetitors([]);
    try {
      const params = {};
      if (dailyCpv) params.cpv = dailyCpv.replace(/\D/g, '');
      const res = await API.get(`/sicap/daily/${dailyDate}`, { params });
      setDailyContracts(res.data);
      setDailyCompetitors(extractCompetitors(res.data.contracts));
      toast.success(`${res.data.pagination?.total || res.data.contracts?.length || 0} contracte, ${extractCompetitors(res.data.contracts).length} furnizori`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setDailyLoading(false); }
  };

  // Documente Constatatoare search
  const searchDocConst = async () => {
    if (!docConstCif.trim()) { toast.error('Introduceti CIF-ul firmei'); return; }
    setDocConstLoading(true); setDocConst(null);
    try {
      const res = await API.get(`/sicap/documente-constatatoare/${docConstCif.replace(/\D/g, '')}`);
      setDocConst(res.data);
      toast.success(`${res.data.total || 0} documente constatatoare gasite`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare SICAP.ai'); }
    finally { setDocConstLoading(false); }
  };

  // AI analysis on extracted competitors
  const analyzeCompetitors = async (comps, context) => {
    if (!comps.length) return;
    setAnalyzing(true);
    try {
      const compText = comps.slice(0, 10).map(c =>
        `- ${c.name} (CUI: ${c.cif}) | ${c.contracts} contracte | ${formatValue(c.totalValue)} | CPV: ${c.cpvs.join(', ')} | Surse: ${c.sources.join(', ')}`
      ).join('\n');
      const res = await API.post('/competitors/analyze-sicap', {
        name: 'Analiza multipla competitori', cui: 'multi',
        seap_wins: comps.reduce((s, c) => s + c.contracts, 0),
        avg_value: Math.round(comps.reduce((s, c) => s + c.totalValue, 0) / comps.length),
        total_value: comps.reduce((s, c) => s + c.totalValue, 0),
        sources: context, cpv_codes: [...new Set(comps.flatMap(c => c.cpvs))].join(', '),
        recent: compText
      });
      setAnalysis(res.data.analysis);
      toast.success('Analiza strategica generata!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare AI'); }
    finally { setAnalyzing(false); }
  };

  const CompetitorCard = ({ comp, rank }) => (
    <div className="flex items-center gap-4 p-3 border-b border-white/5 last:border-0 hover:bg-white/5 rounded transition-colors" data-testid={`competitor-${comp.cif}`}>
      <div className="w-7 h-7 rounded-full bg-rose-500/10 flex items-center justify-center text-xs font-bold text-rose-400 font-mono-data shrink-0">{rank}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{comp.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-muted-foreground font-mono-data">CUI: {comp.cif}</span>
          {comp.sources.map(s => (
            <Badge key={s} variant="outline" className={`text-[9px] border-0 ring-1 ring-inset ${
              s === 'achizitii-directe' ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20' :
              s === 'licitatii-publice' ? 'bg-blue-500/10 text-blue-400 ring-blue-500/20' :
              'bg-amber-500/10 text-amber-400 ring-amber-500/20'
            }`}>{s.replace(/-/g, ' ')}</Badge>
          ))}
          {comp.cpvs.map(cpv => <span key={cpv} className="text-[9px] text-zinc-500 font-mono-data">CPV:{cpv}</span>)}
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold font-mono-data text-emerald-400">{formatValue(comp.totalValue)}</p>
        <p className="text-[10px] text-muted-foreground">{comp.contracts} contracte</p>
      </div>
    </div>
  );

  return (
    <div className="p-8" data-testid="competitor-intelligence-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
          <Target className="w-6 h-6 text-rose-400" strokeWidth={1.5} />Competitor Intelligence
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Identifica firmele competitoare din contractele SICAP.ai - date real-time</p>
        <Badge className="mt-2 bg-emerald-500/10 text-emerald-400 text-[10px]">Live - api.sicap.ai</Badge>
      </div>

      <Tabs defaultValue="authority" className="w-full">
        <TabsList className="bg-zinc-900/50 border border-white/10 mb-6">
          <TabsTrigger value="authority" data-testid="tab-authority"><Building2 className="w-3.5 h-3.5 mr-1.5" />Competitori per Autoritate</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily"><Calendar className="w-3.5 h-3.5 mr-1.5" />Competitori pe Zi / CPV</TabsTrigger>
          <TabsTrigger value="docconst" data-testid="tab-docconst"><FileCheck className="w-3.5 h-3.5 mr-1.5" />Documente Constatatoare</TabsTrigger>
        </TabsList>

        {/* ===== COMPETITORS BY AUTHORITY ===== */}
        <TabsContent value="authority">
          <Card className="bg-card border-white/10 mb-6">
            <CardContent className="p-6">
              <p className="text-xs text-muted-foreground mb-3">
                Introduceti CIF-ul unei <strong className="text-foreground">autoritati contractante</strong> pentru a vedea <strong className="text-rose-400">ce firme castiga contractele</strong> lor. Acestea sunt competitorii vostri.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="md:col-span-2">
                  <Label>CIF Autoritate Contractanta</Label>
                  <Input value={authCif} onChange={e => setAuthCif(e.target.value)} placeholder="Ex: 16054368 (CNAIR - drumuri)"
                    className="mt-1.5 bg-zinc-900/50 border-white/10 font-mono-data" data-testid="auth-cif-input"
                    onKeyDown={e => e.key === 'Enter' && searchAuthority()} />
                </div>
                <Button onClick={searchAuthority} disabled={loading} className="h-10" data-testid="search-auth-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  {loading ? 'Se cauta...' : 'Identifica Competitori'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { label: 'CNAIR (drumuri/autostrazi)', cif: '16054368' },
                  { label: 'Primaria Bucuresti', cif: '4267117' },
                  { label: 'Min. Sanatatii', cif: '4266456' },
                  { label: 'Min. Apararii', cif: '4813229' },
                  { label: 'CFR Infrastructura', cif: '11054529' },
                ].map(ex => (
                  <button key={ex.cif} onClick={() => setAuthCif(ex.cif)}
                    className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-400 hover:bg-primary/10 hover:text-primary transition-colors">
                    {ex.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {competitors.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Users className="w-4 h-4 text-rose-400" />{competitors.length} Competitori Identificati
                  <span className="text-xs text-muted-foreground font-normal">din {contracts?.contracts?.length} contracte</span>
                </h3>
                <Button onClick={() => analyzeCompetitors(competitors, `Contracte autoritate CIF ${authCif}`)} disabled={analyzing} variant="outline" size="sm" data-testid="analyze-competitors-btn">
                  {analyzing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Brain className="w-3.5 h-3.5 mr-1.5" />}
                  Analiza AI Strategica
                </Button>
              </div>

              {/* Top stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-card border-white/10"><CardContent className="p-3 text-center">
                  <p className="text-xl font-bold font-mono-data text-rose-400">{competitors.length}</p>
                  <p className="text-[9px] text-muted-foreground uppercase">Firme Concurente</p>
                </CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-3 text-center">
                  <p className="text-sm font-bold font-mono-data text-emerald-400">{formatValue(competitors.reduce((s, c) => s + c.totalValue, 0))}</p>
                  <p className="text-[9px] text-muted-foreground uppercase">Valoare Totala</p>
                </CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-3 text-center">
                  <p className="text-xl font-bold font-mono-data text-amber-400">{formatValue(competitors[0]?.totalValue || 0)}</p>
                  <p className="text-[9px] text-muted-foreground uppercase">Lider (#{1})</p>
                </CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-3 text-center">
                  <p className="text-xl font-bold font-mono-data text-cyan-400">{[...new Set(competitors.flatMap(c => c.cpvs))].length}</p>
                  <p className="text-[9px] text-muted-foreground uppercase">Categorii CPV</p>
                </CardContent></Card>
              </div>

              {/* Competitor ranking */}
              <Card className="bg-card border-white/10">
                <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Clasament Firme dupa Valoare Contracte</CardTitle></CardHeader>
                <CardContent>
                  {competitors.slice(0, 15).map((comp, i) => <CompetitorCard key={comp.cif} comp={comp} rank={i + 1} />)}
                  {competitors.length > 15 && <p className="text-xs text-muted-foreground text-center pt-3">... si inca {competitors.length - 15} firme</p>}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ===== DAILY COMPETITORS BY CPV ===== */}
        <TabsContent value="daily">
          <Card className="bg-card border-white/10 mb-6">
            <CardContent className="p-6">
              <p className="text-xs text-muted-foreground mb-3">
                Monitorizati <strong className="text-foreground">ce firme castiga contracte zilnic</strong> - filtrati dupa cod CPV pentru a gasi competitorii din domeniul vostru.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <Label>Data</Label>
                  <Input type="date" value={dailyDate} onChange={e => setDailyDate(e.target.value)}
                    className="mt-1.5 bg-zinc-900/50 border-white/10 font-mono-data" data-testid="daily-date" />
                </div>
                <div className="md:col-span-2">
                  <Label>Cod CPV (filtru domeniu)</Label>
                  <Input value={dailyCpv} onChange={e => setDailyCpv(e.target.value)} placeholder="Ex: 72 (IT), 45 (constructii), 33 (medical)"
                    className="mt-1.5 bg-zinc-900/50 border-white/10 font-mono-data" data-testid="daily-cpv" />
                </div>
                <Button onClick={searchDaily} disabled={dailyLoading} className="h-10" data-testid="search-daily-btn">
                  {dailyLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                  Scaneaza Competitori
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { label: 'IT & Software (72)', cpv: '72' },
                  { label: 'Constructii (45)', cpv: '45' },
                  { label: 'Medical (33)', cpv: '33' },
                  { label: 'Hardware (30)', cpv: '30' },
                  { label: 'Securitate (79)', cpv: '79' },
                  { label: 'Consultanta (73)', cpv: '73' },
                ].map(ex => (
                  <button key={ex.cpv} onClick={() => setDailyCpv(ex.cpv)}
                    className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-400 hover:bg-primary/10 hover:text-primary transition-colors">
                    {ex.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {dailyCompetitors.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <TrendingUp className="w-4 h-4 text-amber-400" />{dailyCompetitors.length} Furnizori Activi pe {dailyDate}
                  {dailyContracts?.pagination?.total && <span className="text-xs text-muted-foreground font-normal">({dailyContracts.pagination.total} contracte total in ziua respectiva)</span>}
                </h3>
                <Button onClick={() => analyzeCompetitors(dailyCompetitors, `Contracte ${dailyDate} CPV:${dailyCpv || 'toate'}`)} disabled={analyzing} variant="outline" size="sm">
                  {analyzing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Brain className="w-3.5 h-3.5 mr-1.5" />}
                  Analiza AI
                </Button>
              </div>
              <Card className="bg-card border-white/10">
                <CardContent className="pt-4">
                  {dailyCompetitors.slice(0, 20).map((comp, i) => <CompetitorCard key={comp.cif} comp={comp} rank={i + 1} />)}
                </CardContent>
              </Card>
            </div>
          )}
          {dailyContracts && !dailyCompetitors.length && (
            <div className="text-center py-8">
              <AlertTriangle className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">Niciun contract gasit. Incercati o alta data sau alt CPV.</p>
            </div>
          )}
        </TabsContent>

        {/* ===== DOCUMENTE CONSTATATOARE TAB ===== */}
        <TabsContent value="docconst">
          <Card className="bg-card border-white/10 mb-6">
            <CardContent className="p-6">
              <p className="text-xs text-muted-foreground mb-3">
                <strong className="text-foreground">Documente constatatoare</strong> = certificate de performanta emise de autoritati contractante dupa finalizarea contractelor. Arata calitatea lucrarilor executate.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="md:col-span-2">
                  <Label>CIF Firma (competitor sau propriu)</Label>
                  <Input value={docConstCif} onChange={e => setDocConstCif(e.target.value)} placeholder="Ex: 32184793 (SAGA INFRASTRUCTURA)"
                    className="mt-1.5 bg-zinc-900/50 border-white/10 font-mono-data" data-testid="docconst-cif"
                    onKeyDown={e => e.key === 'Enter' && searchDocConst()} />
                </div>
                <Button onClick={searchDocConst} disabled={docConstLoading} className="h-10" data-testid="search-docconst-btn">
                  {docConstLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck className="w-4 h-4 mr-2" />}
                  {docConstLoading ? 'Se cauta...' : 'Cauta Documente'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { label: 'SAGA INFRASTRUCTURA', cif: '32184793' },
                  { label: 'CNAIR (autoritate)', cif: '16054368' },
                  { label: 'Strabag', cif: '2862243' },
                  { label: 'Porr Construct', cif: '15387498' },
                ].map(ex => (
                  <button key={ex.cif} onClick={() => setDocConstCif(ex.cif)}
                    className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-400 hover:bg-primary/10 hover:text-primary transition-colors">
                    {ex.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {docConst?.items?.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{docConst.total} documente constatatoare</p>
              {docConst.items.map((dc, i) => (
                <Card key={i} className="bg-card border-white/10 hover:border-white/20 transition-all" data-testid={`docconst-${i}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium mb-1">{dc.contractName}</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 ring-emerald-500/20 ring-1 ring-inset border-0">
                            {dc.procedureType || 'N/A'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">Nr: {dc.contractNo}</span>
                          <span className="text-xs text-muted-foreground">{dc.documentDate?.split('T')[0]}</span>
                          {!dc.hasDamages && <Badge className="bg-emerald-500/10 text-emerald-400 text-[9px]"><Award className="w-3 h-3 mr-0.5" />Fara daune</Badge>}
                          {dc.hasDamages && <Badge className="bg-rose-500/10 text-rose-400 text-[9px]">Cu daune!</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                      <div><span className="text-muted-foreground">Furnizor:</span> <span className="font-medium">{dc.supplierName}</span></div>
                      <div><span className="text-muted-foreground">CIF:</span> <span className="font-mono-data">{dc.supplierCif}</span></div>
                      <div><span className="text-muted-foreground">Autoritate:</span> <span>{dc.contractingAuthorityName?.slice(0, 40)}</span></div>
                      <div><span className="text-muted-foreground">Categorie:</span> <span>{dc.ackDocCategory}</span></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {docConst && !docConst.items?.length && (
            <p className="text-center text-sm text-muted-foreground py-8">Niciun document constatator gasit pentru acest CIF</p>
          )}
        </TabsContent>
      </Tabs>

      {/* AI Analysis Result */}
      {analysis && (
        <Card className="bg-card border-white/10 mt-6" data-testid="competitor-analysis">
          <CardHeader><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Brain className="w-4 h-4 text-primary" />Analiza AI Strategica Competitori</CardTitle></CardHeader>
          <CardContent><div className="prose-dark"><ReactMarkdown>{analysis}</ReactMarkdown></div></CardContent>
        </Card>
      )}
    </div>
  );
}
